import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Calendar, MapPin, Copy, CheckCheck } from 'lucide-react';
import { Booking, ROOMS } from '../../types/booking';
import AccessCard from '../../components/appointments/AccessCard';
import { bookingService } from '../../services/bookingService';

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      title="Copy meeting link"
      className="text-gray-400 hover:text-blue-400 transition-colors flex-shrink-0"
    >
      {copied ? <CheckCheck className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

export default function BookingConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as { booking?: Booking; selfie_b64?: string | null } | null;
  const booking = state?.booking;

  const roomName = booking
    ? (ROOMS.find((r) => r.id === booking.room_id)?.name ?? booking.room_id)
    : '';

  // Poll for the generated access card (background task may take 1–3s)
  const { data: cardData } = useQuery({
    queryKey: ['access-card', booking?.id],
    queryFn: () => bookingService.getAccessCard(booking!.id),
    enabled: !!booking?.id,
    refetchInterval: (query) => {
      // Stop polling once card_image_b64 is available
      return query.state.data?.card_image_b64 ? false : 2000;
    },
    retry: false,
  });

  return (
    <div className="max-w-lg mx-auto space-y-6 text-center">
      <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Booking Confirmed!</h1>
      <p className="text-gray-600 dark:text-gray-400">
        Your appointment has been successfully booked. A confirmation email will be sent shortly.
      </p>

      {/* Access card — shown prominently per AC6 */}
      {booking && (
        <div className="space-y-1">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Your Digital Access Card
          </p>
          <AccessCard cardImageB64={cardData?.card_image_b64 ?? null} phase="default" />
        </div>
      )}

      {booking && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 text-left space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <Calendar className="h-4 w-4 text-blue-500" />
            <span>{new Date(booking.appointment_at).toLocaleString('en-ZA', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <MapPin className="h-4 w-4 text-purple-500" />
            <span>{roomName}</span>
          </div>
          {booking.guest_name && (
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Guest: <span className="font-medium">{booking.guest_name}</span>
            </div>
          )}
          <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">Booking reference</p>
            <p className="font-mono text-sm text-gray-900 dark:text-white">{booking.id}</p>
          </div>
          {booking.meeting_url && (
            <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Your meeting link</p>
              <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                <span className="font-mono text-xs text-blue-600 dark:text-blue-400 truncate flex-1">
                  {booking.meeting_url}
                </span>
                <CopyLinkButton url={booking.meeting_url} />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 justify-center">
        <button
          onClick={() => navigate('/trading/appointments')}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          View My Appointments
        </button>
        <button
          onClick={() => navigate('/trading/appointments/book')}
          className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Book Another
        </button>
      </div>
    </div>
  );
}
